import axios from "axios";
import { API_BASE as PUBLIC_API_BASE } from "./publicEventsApi";
import { PART_TYPES, PRICE_UNITS } from "../components/BundleCard/bundleModel";

/**
 * Schritt 3 der Eventstrecke -- die Anreise.
 *
 * Nur der Unterkunftszweig kommt hier an: wer die Pauschalreise gewaehlt hat,
 * hat den Flug bereits im Vertrag des Veranstalters und ueberspringt diesen
 * Schritt.
 *
 * Der Flug ist ein EIGENER Vertrag mit einer eigenen Fluggesellschaft. Er wird
 * hier neben Ticket und Hotel gestellt, nicht mit ihnen verschnuert -- das ist
 * der Unterschied zwischen verbundenen Reiseleistungen und einer Pauschalreise
 * (§ 651w gegen § 651c).
 */

/** Flugsuche des Hauses. Dieselbe Quelle, die auch die Flugstrecke im Client nutzt. */
const FLIGHT_SEARCH_URL = "https://amadeus.holiserver.com/ratehawkapi/api/airtuerk";

/**
 * Flughaefen, die den Veranstaltungsort bedienen.
 *
 * Zweistufig, weil ein Veranstaltungsort selten einen eigenen Flughafen hat:
 * erst der Ort selbst, sonst seine GIATA-Destination. `level` sagt, welche
 * Stufe getroffen hat -- "destination" heisst: der Flughafen liegt in derselben
 * Region, nicht am Ort. Das gehoert dem Reisenden gesagt.
 */
export async function fetchVenueAirports({ cityId, name, country }) {
  const params = cityId ? { city_id: cityId } : { name, ...(country ? { country } : {}) };
  const res = await axios.get(`${PUBLIC_API_BASE}/locations/airports-for-place`, { params });
  const data = res?.data?.data || {};
  return { items: data.items || [], place: data.place || "", destination: data.destination || "" };
}

/**
 * Hin- und Rueckflug suchen.
 *
 * Die Antwort dieser Quelle ist gross (fuer Berlin-Mailand ueber 1.000 Angebote,
 * etliche davon ununterscheidbar) -- deshalb wird sie hier sofort auf das
 * eingedampft, was der Reisende unterscheiden kann. Alles durchzureichen hiesse,
 * die Auswahl an den Browser zu delegieren.
 */
export async function fetchFlightOffers({ from, to, departDate, returnDate, adults }) {
  if (!from || !to) throw new Error("Abflug- und Zielflughafen werden gebraucht.");
  if (!departDate || !returnDate) throw new Error("Hin- und Rueckreisedatum werden gebraucht.");

  const segments = [
    { date: departDate, departure: from, destination: to },
    { date: returnDate, departure: to, destination: from },
  ];

  let res;
  try {
    res = await axios.post(FLIGHT_SEARCH_URL, {
      adults: Number(adults) || 1,
      directflight: false,
      maxstops: 2,
      queryid: `eventflow-${Date.now()}`,
      segments,
    });
  } catch (err) {
    // Wortlaut der Quelle durchreichen statt "Suche fehlgeschlagen" -- sonst
    // sehen "keine Verbindung" und "keine Fluege" gleich aus.
    const detail = err?.response?.data?.error || err?.response?.data?.message || err?.message;
    throw new Error(detail || "Die Flugsuche ist fehlgeschlagen.");
  }

  const offers = Array.isArray(res?.data?.offers) ? res.data.offers : null;
  if (!offers) {
    throw new Error("Die Flugsuche hat keine verwertbare Antwort geliefert.");
  }

  return offers;
}

/** Zahl aus dem Preisknoten. 0 heisst "kein Preis", nicht "kostenlos". */
function amountOf(offer) {
  const value = Number(offer?.PriceInfo?.Price?.value);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function flightNode(offer) {
  const flight = offer?.OfferServices?.Package?.Flight;
  return Array.isArray(flight) ? flight[0] : flight;
}

function legInfo(leg) {
  if (!leg) return null;
  return {
    from: leg?.FlightDeparture?.DepartureAirportRef?.AirportCode || "",
    to: leg?.FlightArrival?.ArrivalAirportRef?.AirportCode || "",
    date: leg?.FlightDeparture?.DepartureDate || "",
    departTime: leg?.FlightDeparture?.DepartureTime || "",
    arriveTime: leg?.FlightArrival?.ArrivalTime || "",
    carrier:
      leg?.OperatingBrand?.Code ||
      leg?.MarketingBrand?.Code ||
      leg?.OperatingBrand?.Name ||
      "",
  };
}

/**
 * Angebote zu unterscheidbaren Verbindungen zusammenfassen.
 *
 * Die Quelle liefert dieselbe Verbindung vielfach (unterschiedliche Tarife,
 * gleiche Zeiten) -- fuer Berlin-Mailand am 04.09. waren es 1.055 Angebote, in
 * denen sich 06:20 mit 318,03 EUR viermal hintereinander wiederholte. Vier
 * gleiche Zeilen sind fuer den Reisenden kein Angebot, sondern Laerm.
 *
 * Zusammengefasst wird nach Hin- und Rueckzeiten plus Fluggesellschaft;
 * behalten wird das guenstigste Angebot je Verbindung, und wie viele Tarife
 * dahinterstanden.
 */
export function groupFlightOffers(offers, limit = 12) {
  const byKey = new Map();

  for (const offer of offers) {
    const node = flightNode(offer);
    const out = legInfo(node?.OutboundFlight);
    const back = legInfo(node?.InboundFlight || node?.ReturnFlight);
    const amount = amountOf(offer);
    if (!out || !amount) continue;

    const key = [out.from, out.to, out.date, out.departTime, back?.date, back?.departTime, out.carrier].join("|");
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { out, back, amount, currency: offer?.PriceInfo?.Price?.currency || "EUR", fares: 1, raw: offer });
    } else {
      existing.fares += 1;
      if (amount < existing.amount) {
        existing.amount = amount;
        existing.raw = offer;
      }
    }
  }

  return [...byKey.values()].sort((a, b) => a.amount - b.amount).slice(0, limit);
}

/** Aus einer Verbindung einen Baustein machen -- wie Ticket und Hotel einer ist. */
export function flightToPart(entry) {
  const { out, back, amount, currency } = entry;

  const lines = [];
  if (out.departTime) lines.push(`Hin ${out.departTime} ab ${out.from}`);
  if (back?.departTime) lines.push(`Rück ${back.departTime} ab ${back.from}`);
  if (entry.fares > 1) lines.push(`${entry.fares} Tarife`);

  return {
    type: PART_TYPES.FLIGHT,
    id: `flight:${out.from}-${out.to}-${out.date}-${out.departTime}-${out.carrier}`,
    title: `${out.from} → ${out.to}${out.carrier ? ` · ${out.carrier}` : ""}`,
    lines: lines.slice(0, 3),
    description: "",
    media: { imageUrl: null, badge: "Flug", caption: out.date || "" },
    // Preis wie geliefert: pro Person. Nicht umgerechnet.
    price: amount ? { amount, currency, unit: PRICE_UNITS.PER_PERSON, from: false } : null,
    provider: out.carrier || "Fluggesellschaft",
    source: "flight",
    sourceId: out.carrier || "",
    anchorData: {},
    constraints: [],
    // Das ROHE Angebot bleibt dran: die Ergebnisanzeige der Flugstrecke
    // (FlightOfferCard) rendert daraus Legs, Umstiege und Zeiten. Es hier
    // wegzuwerfen hiesse, die Darstellung ein zweites Mal zu bauen.
    raw: { outbound: out, inbound: back || null, offer: entry.raw, fares: entry.fares },
  };
}
